'use client';
import {useContext, useEffect, useRef} from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {LayoutContext} from '@/app/ClientRootHomepage';
import CustomSwiper from "@/components/common/CustomSwiper";
import {
    HISTORY_AIMS_ITEMS, HISTORY_BUHL_ITEMS, HISTORY_CELL_ITEMS, HISTORY_CELL_ITEMS01, HISTORY_CELL_ITEMS02,
    HISTORY_DOJIN_ITEMS01, HISTORY_DOJIN_ITEMS02,
    HISTORY_EURO_ITEMS01, HISTORY_EURO_ITEMS02,
    HISTORY_FDA_ITEMS01, HISTORY_FDA_ITEMS02,
    HISTORY_INNO_ITEMS,
    HISTORY_MET_ITEMS, HISTORY_MET_ITEMS01, HISTORY_MET_ITEMS02,
    HISTORY_NEXT_BIO_ITEMS01, HISTORY_NEXT_BIO_ITEMS02,
    HISTORY_NGS_ITEMS01, HISTORY_NGS_ITEMS02,
    HISTORY_RD_ITEMS,
    HISTORY_SYSTEM_ITEMS
} from "@/app/homepage/company/controller";
import {SIZES} from "@/components/constants";
import {getScroller, refreshOnAssetsReady, setupLenyStyle} from "@/app/homepage/utils";
import {useMediaQuery} from "react-responsive";

gsap.registerPlugin(ScrollTrigger);
const History = () => {
    const wrapRef = useRef(null);
    const { rootRef } = useContext(LayoutContext);
    const isTablet = useMediaQuery({
        query: `(max-width: ${SIZES.TABLET})`
    });

    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const scroller = getScroller(rootRef);
        const contentEl =
            (scroller && scroller.querySelector && scroller.querySelector(".root")) ||
            scroller?.firstElementChild ||
            scroller;

        const { cleanup } = setupLenyStyle({ wrap, scroller, contentEl });

        const progressEl = wrap.querySelector(".timeline-progress");
        const dots = Array.from(wrap.querySelectorAll(".timeline-item .dot"));
        const items = Array.from(wrap.querySelectorAll(".timeline-item"));
        if (!progressEl || !dots.length || !items.length) {
            cleanup();
            return;
        }

        const getScrollTop = () =>
            scroller === window
                ? window.scrollY || document.documentElement.scrollTop || 0
                : scroller.scrollTop || 0;

        const getCenterLineY = () => {
            const topGap = document.querySelector(".site-page-header")?.offsetHeight || 0;
            if (scroller === window) {
                const viewTop = 0 + topGap;
                const viewH   = window.innerHeight - topGap;
                return viewTop + viewH / 2;
            } else {
                const rect    = scroller.getBoundingClientRect();
                const viewTop = rect.top + topGap;
                const viewH   = scroller.clientHeight - topGap;
                return viewTop + viewH / 2;
            }
        };

        items.forEach((it) => {
            it.classList.remove("active");
            const box = it.querySelector(".box-content");
            if (box) {
                gsap.set(box, { y: 24, opacity: 0 });
                box.dataset.animState = "hidden";
            }
        });
        dots.forEach((d) => d.classList.remove("active"));
        gsap.set(progressEl, { scaleY: 0, transformOrigin: "top center" });

        let crossedFirst = false;
        const updateAll = () => {
            const wrapRect = wrap.getBoundingClientRect();
            const centerY  = getCenterLineY();
            const top      = getScrollTop();

            const firstDot  = dots[0];
            const firstItem = items[0];
            const firstBox  = firstItem?.querySelector(".box-content");
            if (!firstDot || !firstItem) return;

            const r0 = firstDot.getBoundingClientRect();
            const firstDotCenterY = r0.top + r0.height / 2;

            if (top <= 1) crossedFirst = false;
            const passedFirstNow = top > 1 && centerY >= firstDotCenterY;
            if (passedFirstNow) crossedFirst = true;

            const endY = crossedFirst ? centerY : firstDotCenterY;

            let covered = endY - wrapRect.top;
            covered = Math.max(0, Math.min(covered, wrapRect.height));
            const scaleTarget = wrapRect.height ? covered / wrapRect.height : 0;

            gsap.to(progressEl, {
                scaleY: scaleTarget,
                duration: crossedFirst ? 0.18 : 0,
                ease: "power1.out",
                overwrite: true,
            });

            firstDot.classList.add("active");
            firstItem.classList.add("active");
            if (firstBox && firstBox.dataset.animState !== "shown") {
                gsap.to(firstBox, { y: 0, opacity: 1, duration: 0.45, ease: "power2.out", force3D: false });
                firstBox.dataset.animState = "shown";
            }

            dots.forEach((dot, idx) => {
                if (idx === 0) return;
                const r = dot.getBoundingClientRect();
                const dotCenterY = r.top + r.height / 2;
                const active = centerY >= dotCenterY;

                dot.classList.toggle("active", active);

                const itemEl = dot.closest(".timeline-item");
                if (itemEl) itemEl.classList.toggle("active", active);

                const box = itemEl?.querySelector(".box-content");
                if (!box) return;

                if (active) {
                    if (box.dataset.animState !== "shown") {
                        gsap.to(box, { y: 0, opacity: 1, duration: 0.6, ease: "power2.out", force3D: false });
                        box.dataset.animState = "shown";
                    }
                } else {
                    if (box.dataset.animState !== "hidden") {
                        gsap.to(box, { y: 24, opacity: 0, duration: 0.25, ease: "power1.out", force3D: false });
                        box.dataset.animState = "hidden";
                    }
                }
            });
        };

        const st = ScrollTrigger.create({
            trigger: wrap,
            start: "top bottom",
            end: "bottom top",
            onUpdate: () => updateAll(),
            onRefresh: () => updateAll(),
        });

        const onResize = () => {
            ScrollTrigger.refresh();
            updateAll();
        };
        window.addEventListener("resize", onResize);

        // assets 로드 후 1회 보정 (유틸)
        const offAssets = refreshOnAssetsReady(wrap, () => {
            ScrollTrigger.refresh();
            requestAnimationFrame(() => {
                updateAll();
                requestAnimationFrame(() => updateAll());
            });
        });

        let infoTween, infoST;
        const info = wrap.parentElement?.querySelector(".history-info-wrap");
        if (info) {
            gsap.set(info, { y: 30, opacity: 0 });
            infoTween = gsap.to(info, {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: "power2.out",
                paused: true,
                force3D: false,
            });

            infoST = ScrollTrigger.create({
                trigger: info,
                start: "top 70%",
                end: "top 45%",
                toggleActions: "play none none reverse",
                onEnter: () => infoTween.play(),
                onEnterBack: () => infoTween.play(),
                onLeaveBack: () => infoTween.reverse(),
            });
        }

        let groupImgPins = [];

        const getTopGap = () => {
            return document.querySelector(".site-page-header")?.offsetHeight || 0; // 고정 헤더 + 상단 브레드크럼 등
        };

        const groupImg = gsap.utils.toArray(".timeline-groups .groups-img");
        if (groupImg && groupImg.length) {
            const scrollerEl  = getScroller(rootRef);
            const useScroller = (scrollerEl && scrollerEl !== window) ? scrollerEl : undefined;

            groupImg.forEach((img) => {
                const group = img.closest(".timeline-groups");
                if (!group) return;
                const getEndOffset = () => {
                    const imgH = img.offsetHeight || img.getBoundingClientRect().height || 0;
                    return getTopGap() + imgH;
                };
                const st = ScrollTrigger.create({
                    scroller: useScroller,
                    trigger: group,
                    start: () => `top-=${getTopGap() + 50} top`,
                    end:   () => `bottom-=${getEndOffset() + 60} top`,
                    pin: img,
                    pinType: "transform",
                    pinSpacing: false,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    onRefreshInit: () => {
                        img.style.left = "";
                        img.style.right = "";
                        img.style.top = "";
                        img.style.width = "";
                    },
                });

                groupImgPins.push(st);
            });
        }
        return () => {
            st.kill();
            window.removeEventListener("resize", onResize);
            offAssets && offAssets();
            if (infoST) infoST.kill();
            if (infoTween) infoTween.kill();
            groupImgPins.forEach(function(p){ if (p && p.kill) p.kill(); });
            groupImgPins = [];
            cleanup();
        };
    }, [rootRef]);

    return (
        <HistoryWrap>
            <TimelineWrap ref={wrapRef}>
                <div className="timeline-rail"/>
                <div className="timeline-progress"/>
                <div className="list">
                    <div className={"timeline-groups"}>
                        <div className={"groups-data"}>
                            <div className={"timeline-item"}>
                                <div className={"row mo-order"}>
                                    <div className={"box-content"}>
                                        <div className={"text-box tx-al-right"}>
                                            <h1>Metra Biosystems(미국)</h1>
                                            <p>대리점 계약</p>
                                            <span>
                                            Bone Bio-marker 도입<br/>
                                            골다공증의 New 진단·예방·치료 모니터링 체계 구축<br/>
                                            당시 “모든 갱년기 여성을 환자로 만든다”는 상업적 비판과 오해 존재<br/>
                                            오늘날 Bone Marker는 표준 진단 도구로 자리매김
                                        </span>
                                        </div>
                                        <div className={"box-img-lst item01"}>
                                            {
                                                !isTablet ?
                                                    <div className={"pc-bio-met-box"}>
                                                        <div className={"bio-met01"}>
                                                            {
                                                                HISTORY_MET_ITEMS01.map((item, idx) =>
                                                                    <div className={"list-inner"} key={idx}>
                                                                        <img src={item.imageItem}/>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                        <div className={"bio-met02"}>
                                                            {
                                                                HISTORY_MET_ITEMS02.map((item, idx) =>
                                                                    <div className={"list-inner"} key={idx}>
                                                                        <img src={item.imageItem}/>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    :
                                                    <CustomSwiper
                                                        slides={HISTORY_MET_ITEMS}
                                                        effect="slide"
                                                        spaceBetween={8}
                                                        slidesPerView={"1.7"}
                                                    />
                                            }
                                        </div>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className="year-content">
                                        <h3 className="year">1993</h3>
                                    </div>
                                </div>
                            </div>
                            <div className={"timeline-item"}>
                                <div className="row">
                                    <div className="year-content jc-end">
                                        <h3 className="year ">1995</h3>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className={"box-content"}>
                                        <div className="text-box">
                                            <h1>R&D Systems(미국)</h1>
                                            <p>대리점 계약</p>
                                            <span>사이토카인을 통해 면역 진단의 시대를 앞당겨<br />ELISA KIT, Protein, Antibody 제품을 도입<br/>병원균을 이겨내는 힘, 면역력의 시대로 진입</span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            {
                                                !isTablet ?
                                                    <div className={"pc-img-list list-1995"}>
                                                        {
                                                            HISTORY_RD_ITEMS.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    :

                                                    <CustomSwiper
                                                        slides={HISTORY_RD_ITEMS}
                                                        effect="slide"
                                                        spaceBetween={8}
                                                        slidesPerView={"1.4"}
                                                    />

                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={"timeline-item"}>
                                <div className="row mo-order">
                                    <div className={"box-content"}>
                                        <div className={"text-box tx-al-right"}>
                                            <h1>Innogenetics(벨기에)</h1>
                                            <p>대리점 계약</p>
                                            <span>Inno-LiPA HLA 키트 도입<br/>복잡한 혈청검사를 간편.정확한 분자진단으로 대체<br/>정확도 향상으로 이식 부작용 획기적 감소<br/>Korean HLA Pattern S/W 자체 개발 </span>
                                        </div>
                                        <div className={"box-img-lst"}>

                                            {
                                                !isTablet ?
                                                    <div className={"pc-img-list jc-end list-1996"}>
                                                        {
                                                            HISTORY_INNO_ITEMS.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    :

                                                    <CustomSwiper
                                                        slides={HISTORY_INNO_ITEMS}
                                                        effect="slide"
                                                        spaceBetween={8}
                                                        slidesPerView={"1.9"}
                                                    />

                                            }

                                        </div>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className="year-content">
                                        <h3 className="year ">1996</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={"timeline-groups"}>
                        <div className={"groups-data"}>
                            <div className="timeline-item">
                                <div className="row mo-order">
                                    <div className="year-content jc-end">
                                        <h3 className="year">2000</h3>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className={"box-content"}>

                                        <div className="text-box">
                                            <h1>Euroimmun(독일)</h1>
                                            <p>대리점 계약</p>
                                            <span>
                                                퇴행성 관절염 진단에 New Paradigm 전환<br/>
                                                LIA 키트 도입으로 20개 이상 자가항체 동시 분석 가능<br/>
                                                Bio-Chip기술 FANA검사 도입<br/>
                                                자가면역질환 인식 확대 및 검사 일상화에 기여
                                            </span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            <div className={"pc-img-list list-2000"}>
                                                <div className={"list-2000-inner-box"}>
                                                    <div className={"list-2000-item01"}>
                                                        {
                                                            HISTORY_EURO_ITEMS01.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    <div className={"list-2000-item02"}>
                                                        {
                                                            HISTORY_EURO_ITEMS02.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/*<ImageSource>*/}
                                        {/*    <div className={"title"}>출처</div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span>https://www.euroimmunblog.com/category/science/</span>*/}
                                        {/*    </div>*/}
                                        {/*</ImageSource>*/}
                                    </div>

                                </div>
                            </div>
                            <div className="timeline-item">
                                <div className="row mo-order">
                                    <div className={"box-content"}>

                                        <div className="text-box tx-al-right">
                                            <h1>R&D Systems와 K-Bio</h1>
                                            <p>성장 동행</p>
                                            <span>
                                                Biosimilar drug 개발 본격화 시점에<br/>
                                                고품질 Protein-Kit-Antibody를 적기에 대량 공급<br/>
                                                K-Bio성장에 작지만 의미 있는 역할 수행
                                            </span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            {
                                                !isTablet ?
                                                    <div className={"pc-img-list jc-end list-2005"}>
                                                        {
                                                            HISTORY_SYSTEM_ITEMS.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    :

                                                    <CustomSwiper
                                                        slides={HISTORY_SYSTEM_ITEMS}
                                                        effect="slide"
                                                        spaceBetween={8}
                                                        slidesPerView={"1.8"}
                                                    />
                                            }
                                        </div>
                                        {/*<ImageSource className={"right"}>*/}
                                        {/*    <div className={"title"}>출처</div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span className={"w-460"}>https://www.rndsystems.com/products/proteins/ai-modified-proteins?_gl=1*187fjdc*_up*MQ..*_ga*MTg3NjAwOTE1Ny4xNzU4MjQ5OTA1*_ga_2T0XXM39JP*czE3NTgyNDk5MDMkbzEkZzAkdDE3NTgyNDk5MDMkajYwJGwwJGgw</span>*/}
                                        {/*    </div>*/}
                                        {/*</ImageSource>*/}
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className="year-content">
                                        <h3 className="year">2005</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-item">
                                <div className="row mo-order">
                                    <div className={"year-content jc-end"}>
                                        <h3 className="year">2007</h3>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className={"box-content"}>

                                        <div className="text-box">
                                            <h1>Cellestis(호주)</h1>
                                            <p>대리점 계약</p>
                                            <span>
                                                Quanti-FERON® TB kit 도입으로 잠복결핵 치료 시대로 전환<br/>
                                                혈액으로 이틀 만에 잠복결핵 진단 가능<br/>
                                                한국 결핵관리지침(TB Guideline) 개정에 기여<br/>
                                                한국 결핵 발병률 감소에 크게 기여
                                            </span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            {
                                                !isTablet ?
                                                    <div className={"list-2007"}>
                                                        <div className={"list-2007-box"}>
                                                            <div className={"list-2007-item01"}>
                                                                {
                                                                    HISTORY_CELL_ITEMS01.map((item, idx) =>
                                                                        <div className={"list-inner"} key={idx}>
                                                                            <img src={item.imageItem}/>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                            <div className={"list-2007-item02"}>
                                                                {
                                                                    HISTORY_CELL_ITEMS02.map((item, idx) =>
                                                                        <div className={"list-inner"} key={idx}>
                                                                            <img src={item.imageItem}/>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>

                                                        </div>
                                                    </div>
                                                    :

                                                    <CustomSwiper
                                                        slides={HISTORY_CELL_ITEMS}
                                                        effect="slide"
                                                        spaceBetween={8}
                                                        slidesPerView={"2.3"}
                                                    />
                                            }
                                        </div>
                                        {/*<ImageSource>*/}
                                        {/*    <div className={"title"}>출처</div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span>https://www.qiagen.com/kr/customer-stories/the-hidden-tb-epidemic-in-ukraine</span>*/}
                                        {/*    </div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span>https://www.qiagen.com/us/tb-testing/tb-blood-test-vs-skin-test</span>*/}
                                        {/*    </div>*/}
                                        {/*</ImageSource>*/}
                                    </div>

                                </div>
                            </div>
                            <div className="timeline-item">
                                <div className="row mo-order">
                                    <div className={"box-content"}>

                                        <div className="text-box tx-al-right">
                                            <h1>Dojindo(일본)</h1>
                                            <p>대리점 계약</p>
                                            <span>CCK-8 도입하여 고독성 저품질 MTT assay를 대체<br/>더 안전하고 정확한 Cell Viability 평가 기준 제시</span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            <div className={"list-2009"}>
                                                <div className={"list-2009-box"}>
                                                    <div className={"list-2009-item01"}>
                                                        {
                                                            HISTORY_DOJIN_ITEMS01.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    <div className={"list-2009-item02"}>
                                                        {
                                                            HISTORY_DOJIN_ITEMS02.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                        {/*<ImageSource className={"right"}>*/}
                                        {/*    <div className={"title"}>출처</div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span>https://aimbiotech.com/vasq-kit/</span>*/}
                                        {/*    </div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span>https://www.dojindo.com/ASIA/products/CK04/</span>*/}
                                        {/*    </div>*/}
                                        {/*</ImageSource>*/}
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className="year-content">
                                        <h3 className="year">2009</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"timeline-groups"}>
                        <div className={"groups-data"}>
                            <div className="timeline-item">
                                <div className="row">
                                    <div className="year-content jc-end">
                                        <h3 className="year">2012</h3>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className={"box-content"}>
                                        <div className="text-box">
                                            <h1>BUHLMANN(스위스)</h1>
                                            <p>대리점 계약</p>
                                            <span>Calprotectin POCT검사 도입<br/>IBD(염증성 장질환) 환자 조기 진단 가능<br/>IBD 환자 임상 노출과 치료 개시에 기여</span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            {
                                                !isTablet ?
                                                    <div className={"pc-img-list list-2012"}>
                                                        {
                                                            HISTORY_BUHL_ITEMS.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    :
                                                    <CustomSwiper
                                                        slides={HISTORY_BUHL_ITEMS}
                                                        effect="slide"
                                                        spaceBetween={8}
                                                        slidesPerView={"1.8"}
                                                    />
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-item">
                                <div className="row  mo-order">
                                    <div className={"box-content"}>
                                        <div className={"text-box tx-al-right"}>
                                            <h1>ThermoFisher NGS부문(미국)</h1>
                                            <p>대리점 계약</p>
                                            <span>
                                                암의 정밀진단을 통해 개인 맞춤형 치료 기반 마련<br/>
                                                유전질환의 빠르고 정확한 진단<br/>
                                                특정 치료제에 맞춘 동반진단 검사 도입<br/>
                                                NGS진단 자동화 및 워크플로우 최적화
                                            </span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            {
                                                !isTablet ?
                                                    <div className={"list-2015"}>
                                                        <div className={"list-2015-box"}>
                                                            <div className={"list-2015-item01"}>
                                                                {
                                                                    HISTORY_NGS_ITEMS01.map((item, idx) =>
                                                                        <div className={"list-inner"} key={idx}>
                                                                            <img src={item.imageItem}/>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                            <div className={"list-2015-item02"}>
                                                                {
                                                                    HISTORY_NGS_ITEMS02.map((item, idx) =>
                                                                        <div className={"list-inner"} key={idx}>
                                                                            <img src={item.imageItem}/>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className={"list-2015"}>
                                                        <CustomSwiper
                                                            slides={HISTORY_NGS_ITEMS01}
                                                            effect="slide"
                                                            spaceBetween={8}
                                                            slidesPerView={"2.3"}
                                                        />
                                                        <div className={"list-2015-item02"}>
                                                            {
                                                                HISTORY_NGS_ITEMS02.map((item, idx) =>
                                                                    <div className={"list-inner"} key={idx}>
                                                                        <img src={item.imageItem}/>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                            }

                                        </div>
                                        {/*<ImageSource className={"right"}>*/}
                                        {/*    <div className={"title"}>출처</div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span*/}
                                        {/*            className={"w-460"}>https://www.thermofisher.com/kr/ko/home/clinical/diagnostic-testing/instruments-automation/genetic-analysis-instruments/genexus-dx-system.html</span>*/}
                                        {/*    </div>*/}
                                        {/*</ImageSource>*/}
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className="year-content">
                                        <h3 className="year ">2015</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-item">
                                <div className="row">
                                    <div className="year-content jc-end">
                                        <h3 className="year ">2018</h3>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className={"box-content"}>
                                        <div className="text-box">
                                            <h1>AimBiotech (싱가포르)</h1>
                                            <p>대리점 계약</p>
                                            <span>Organ-on-a-Chip 도전 시작<br/>2D 배양에서 3D 인체 유사 환경으로 전환 시도<br/>Biology-on-a-Chip 기반 마련 도전</span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            {
                                                !isTablet ?
                                                    <div className={"pc-img-list"}>
                                                        {
                                                            HISTORY_AIMS_ITEMS.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    :
                                                    <CustomSwiper
                                                        slides={HISTORY_AIMS_ITEMS}
                                                        effect="slide"
                                                        spaceBetween={16}
                                                        slidesPerView={"2.1"}
                                                    />
                                            }
                                        </div>
                                        {/*<ImageSource>*/}
                                        {/*    <div className={"title"}>출처</div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span>https://aimbiotech.com/vasq-kit/</span>*/}
                                        {/*    </div>*/}
                                        {/*</ImageSource>*/}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"timeline-groups"}>
                        <div className={"groups-data"}>
                            <div className="timeline-item">
                                <div className="row mo-order">
                                    <div className={"box-content"}>
                                        <div className="text-box tx-al-right">
                                            <h1>넥스트앤바이오(한국)</h1>
                                            <p>대리점 계약</p>
                                            <span>
                                                암종별 미니 장기 모델(Cancer-derived Organoid) Bank 설립<br/>
                                                환자별 기존 암치료제 사전 약물 감수성 정밀 진단 가능<br/>
                                                동물실험 대체 가능<br/>
                                                개발 기간·비용 절감
                                            </span>
                                        </div>

                                        <div className={"box-img-lst"}>
                                            <div className={"list-2021"}>
                                                <div className={"list-2021-box"}>
                                                    <div className={"list-2021-item01"}>
                                                        {
                                                            HISTORY_NEXT_BIO_ITEMS01.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    <div className={"list-2021-item02"}>
                                                        {
                                                            HISTORY_NEXT_BIO_ITEMS02.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                        {/*<ImageSource className={"right"}>*/}
                                        {/*    <div className={"title"}>출처</div>*/}
                                        {/*    <div className={"source-inner"}>*/}
                                        {/*        <span>&gt;</span>*/}
                                        {/*        <span>http://nextandbio.com/technology/</span>*/}
                                        {/*    </div>*/}
                                        {/*</ImageSource>*/}
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className="year-content">
                                        <h3 className="year">2021</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-item">
                                <div className="row mo-order">
                                    <div className="year-content jc-end">
                                        <h3 className="year">2023</h3>
                                    </div>
                                    <span className="dot" aria-hidden/>
                                    <div className={"box-content"}>
                                        <div className="text-box">
                                            <h1>(주)엑셀세라퓨틱스(한국)</h1>
                                            <p>대리점 계약</p>
                                            <span>
                                                동물유래 성분 완전 제외 AOF 배양 배지 개발<br/>
                                                FDA DMF 등록 (원료의약품 마스터 파일)<br/>
                                                세포치료제 유전자치료제 CGT시대 도입 가속화 동반
                                            </span>
                                        </div>
                                        <div className={"box-img-lst"}>
                                            <div className={"list-2023"}>
                                                <div className={"list-2023-box"}>
                                                    <div className={"list-2023-item01"}>
                                                        {
                                                            HISTORY_FDA_ITEMS01.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    <div className={"list-2023-item02"}>
                                                        {
                                                            HISTORY_FDA_ITEMS02.map((item, idx) =>
                                                                <div className={"list-inner"} key={idx}>
                                                                    <img src={item.imageItem}/>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TimelineWrap>
            <div className={"history-info-wrap"}>
                <div className={"text-inner"}>
                    {
                        !isTablet ?
                            <>
                                1993년 새로운 기술과 병원 경영 선진화 흐름에 맞추어 면역의 중요성을 인식하고<br/>
                                진단 시장에서 틈새 시장을 파고 들어 새로운 진단제품을 도입하여 진단의 새로운 영역을 개척을 목표로 창업하였습니다.<br/><br/>
                                진단의 경험을 활용하여 바이오연구 영역으로 넘어가 면역연구와 신약개발 분야에 뛰어들어<br/>
                                복제약 개발 수준의 제약바이오 시장이 현재의 K-Bio위상에 오르는데 자그나마 일익을 담당했습니다.<br/><br/>
                                <p>
                                    새로운 바이오-진단 기술을 3~5년 앞당겨 도입한다는 개척정신으로<br/>
                                    "내 가족은 물론 대한민국의 국민의 건강과 생명을 보호"한다는 사명을 가지고<br/>
                                    웅비 전직원은 오늘도 병원 현장으로, 연구실로, 바이오제약사로 힘써 소중한 고객을 찾아가고 있습니다.<br/><br/>
                                </p>
                                <span>진단과 분석을 넘어 건강을 향해서.</span>
                            </>
                            :
                            <>
                                1993년 새로운 기술과 병원 경영 선진화 흐름에 맞추어 면역의 중요성을 인식하고
                                진단 시장에서 틈새 시장을 파고 들어 새로운 진단제품을 도입하여 진단의 새로운 영역을 개척을 목표로 창업하였습니다.<br/><br/>
                                진단의 경험을 활용하여 바이오연구 영역으로 넘어가 면역연구와 신약개발 분야에 뛰어들어
                                복제약 개발 수준의 제약바이오 시장이 현재의 K-Bio위상에 오르는데 자그나마 일익을 담당했습니다.<br/><br/>
                                <p>
                                    새로운 바이오-진단 기술을 3~5년 앞당겨 도입한다는 개척정신으로
                                    "내 가족은 물론 대한민국의 국민의 건강과 생명을 보호"한다는 사명을 가지고
                                    웅비 전직원은 오늘도 병원 현장으로, 연구실로, 바이오제약사로 힘써 소중한 고객을 찾아가고 있습니다.<br/><br/>
                                </p>
                                <span>진단과 분석을 넘어 건강을 향해서.</span>
                            </>
                    }
                </div>
            </div>
        </HistoryWrap>
    );
};


const HistoryWrap = styled.div`
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    .history-info-wrap{
        font-weight: 400;
        font-size: 24px;
        line-height: 38px;
        text-align: center;
        color: #000000;
        margin-bottom: 240px;
        p{
            font-weight: 700;
        }
        span{
            font-size: 30px;
            font-weight: 700;
            color: #4939B1;
        }
    }
    @media (max-width: ${SIZES.TABLET}) {
        .history-info-wrap{
            padding: 0 16px;
            font-weight: 400;
            font-size: 18px;
            line-height: 28px;
            text-align: justify;
            span{
                font-size: 22px;
                font-weight: 700;
                color: #4939B1;
            }
        }
    }
`

const TimelineWrap = styled.section`
    position: relative;
    width: 100%;
    margin: 80px 0 64px;

    .timeline-rail {
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 2px;
        transform: translateX(-50%);
        background: #ccc;
        pointer-events: none;
    }

    .timeline-progress {
        position: absolute;
        top: 0;
        left: 50%;
        width: 2px;
        height: 100%;
        transform: translateX(-50%) scaleY(0);
        transform-origin: top center;
        background: #4939B1;
        will-change: transform;
        pointer-events: none;
    }
  
    .list {
        width: calc(100% - 228px);
        margin: 0 auto;

        .timeline-groups{
            position: relative;
            .groups-img{
                position: absolute;
                left: 0;
                top: 0;
                width: 50%;
                padding-right: 200px;
                z-index: 1;
                &.right{
                    left: auto;
                    right: 0;
                    padding-right: 0;
                    padding-left: 200px;
                }
            }
            .groups-data{
                .timeline-item{
                    .row {
                        display: grid;
                        grid-template-columns: 1fr 20px 1fr;
                        gap: 35px;
                        align-items: start;
                        //padding-top: 50px;

                        .year-content {
                            display: flex;
                            gap: 44px;
                            min-width: 0;

                            .year {
                                font-weight: 700;
                                font-size: 54px;
                                line-height: 64px;
                                letter-spacing: -0.02em;
                                color: #CCCCCC;
                            }
                        }

                        .box-content {
                            min-width: 0;
                            padding-bottom: 66px;

                            .text-box {
                                h1 {
                                    font-weight: 700;
                                    font-size: 24px;
                                    line-height: 36px;
                                    letter-spacing: -0.02em;
                                    color: #23203B;
                                    margin-bottom: 8px;
                                }

                                p {
                                    font-weight: 700;
                                    font-size: 20px;
                                    line-height: 30px;
                                    letter-spacing: -0.02em;
                                    color: #4939B1;
                                    margin-bottom: 32px;
                                }

                                span {
                                    font-weight: 400;
                                    font-size: 16px;
                                    line-height: 24px;
                                    color: #333333;
                                }
                            }

                            .box-img-lst {
                                margin-top: 24px;
                                width: 100%;

                                .pc-img-list {
                                    display: flex;
                                    gap: 16px;
                                    align-items: center;
                                    &.list-1995{
                                        padding-right: 190px;
                                    }
                                    &.list-1996{
                                        padding-left: 70px;
                                    }
                                    &.list-2000{
                                        padding-right: 390px;
                                        .list-2000-inner-box{
                                            .list-2000-item01{
                                                display: flex;
                                                align-items: center;
                                                gap: 16px;
                                            }
                                            .list-2000-item02{
                                                padding-right: 34px;
                                            }
                                        }
                                    }
                                    &.list-2005{
                                        padding-left: 300px;
                                    }
                                    &.list-2012{
                                        padding-right: 114px;
                                    }
                                    
                                    &.cell {
                                        flex-direction: column;

                                        .cell {
                                            display: flex;
                                            gap: 16px;
                                        }
                                    }
                              
                                }

                                
                                .list-2023{
                                    .list-2023-box{
                                        padding-right: 340px;
                                        .list-2023-item01{
                                            display: flex;
                                            align-items: center;
                                            gap: 16px;
                                        }
                                        .list-2023-item02{
                                            padding-right: 150px;
                                        }
                                    }
                                }
                                
                                .list-2021{
                                    .list-2021-box{
                                        padding-left: 240px;
                                        .list-2021-item01{
                                            display: flex;
                                            align-items: center;
                                            gap: 16px;
                                        }
                                        .list-2021-item02{
                                            padding-left: 45px;
                                        }
                                    }   
                                }
                                
                                .list-2015{
                                    .list-2015-box{
                                        .list-2015-item01{
                                            display: flex;
                                            align-items: center;
                                            gap: 16px;
                                        }
                                        .list-2015-item02{
                                            padding-left: 315px;
                                            margin-top: 8px;
                                        }
                                    }
                                }
                                
                                
                                .list-2009{
                                    padding-left: 210px;
                                    .list-2009-box{
                                        .list-2009-item01{
                                            display: flex;
                                            justify-content: end;
                                            gap: 16px;
                                        }
                                        .list-2009-item02{
                                            padding-left: 150px;
                                            margin-top: 8px;
                                        }
                                    }
                                }
                                .list-2007{
                                    padding-right: 4px;
                                    .list-2007-box{
                                        .list-2007-item01, .list-2007-item02{
                                            display: flex;
                                            gap: 16px;
                                            align-items: center;
                                        }
                                        .list-2007-item02{
                                            margin-top: 8px;
                                        }
                                    }
                                }
                                
                                .pc-bio-met-box{
                                    padding-left: 100px;
                                    .bio-met01, .bio-met02{
                                        display: flex;
                                        align-items: center;
                                        gap: 16px;
                                    }
                                    .bio-met02{
                                        margin-top: 8px;
                                        padding-left: 140px;
                                    }
                                }
                                .swiper{
                                    .swiper-wrapper{
                                        align-items: center;
                                    }
                                    .swiper-slide{
                                        img {
                                            display: block;
                                            width: 100%;
                                            height: auto;
                                            max-width: none;
                                        }
                                    }
                                }
                            }
                            &.line-item{
                                display: flex;
                                align-items: end;
                                gap: 24px;
                                .box-img-lst{
                                    flex-shrink: 0;
                                    margin: 0;
                                    width: 60%;
                                }
                            }
                        }

                        .dot {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            background: #CCCCCC;
                            justify-self: center;
                            position: relative;
                            z-index: 1;
                            transition: background-color .2s ease-in-out;
                            margin-top: 30px;
                            &.active {
                                background: #4939B1;
                            }
                        }
                    }
                    &.active{
                        .row{
                            .year-content{
                                .year{
                                    color: #4939B1;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    @media (max-width: ${SIZES.TABLET}) {
        margin: 40px 0 80px;
        .timeline-rail {
            left: 27px;
            transform: translateX(0);
        }
        .timeline-progress {
            left: 27px;
            transform: translateX(0) scaleY(0);
        }

        .list {
            width: 100%;
            padding-left: 27px;
            .timeline-groups{
                .groups-img{
                    display: none;
                }
                .groups-data{
                    .timeline-item{
                        .row {
                            display: flex;
                            position: relative;
                            flex-direction: column;
                            gap: 0;
                            padding: 0 0 0 20px;
                            &.mo-order{
                                .year-content{
                                    order: -1;
                                }
                                .box-content{
                                    .text-box{
                                        order: -1;
                                        &.tx-al-right{
                                            text-align: left;
                                        }
                                    }
                                }
                            }
                            .year-content {
                                gap: 0;
                                display: block;
                                .row-img-wrap{
                                    display: none;
                                }
                                .year {
                                    font-size: 32px;
                                    line-height: 48px;
                                    letter-spacing: -0.02em;
                                }
                            }

                            .box-content {
                                padding-bottom: 60px;
                                width: 100%;
                                .text-box {
                                    padding-right: 16px;
                                    h1 {
                                        font-size: 18px;
                                        line-height: 28px;
                                        margin: 0;
                                    }
                                    p {
                                        font-size: 14px;
                                        line-height: 22px;
                                        margin-bottom: 24px;
                                    }
                                }

                                .box-img-lst {
                                    .pc-img-list {
                                        gap: 8px;
                                        &.list-2000{
                                            padding-right: 72px;
                                            .list-2000-inner-box{
                                                .list-2000-item01{
                                                    gap: 8px;
                                                    padding-right: 30px;
                                                }
                                                .list-2000-item02{
                                                    padding-right: 0;
                                                    margin-top: 4px;
                                                }
                                            }
                                        }
                                    }
                                    .list-2023{
                                        .list-2023-box{
                                            padding-right: 60px;
                                            .list-2023-item01{
                                                gap: 8px;
                                            }
                                            .list-2023-item02{
                                                padding-right: 160px;
                                            }
                                        }
                                    }
                                    .list-2021{
                                        .list-2021-box{
                                            padding-left: 0;
                                            .list-2021-item01{
                                                padding-right: 20px;
                                                gap: 8px;
                                            }
                                            .list-2021-item02{
                                                margin-top: 4px;
                                                padding-left: 0;
                                                padding-right: 8px;
                                            }
                                        }
                                    }
                                    .list-2015{
                                        .list-2015-item02{
                                            padding-right: 160px;
                                            margin-top: 4px;
                                        }
                                    }
                                    .list-2009{
                                        padding-left: 0;
                                        padding-right: 10px;
                                        .list-2009-box{
                                            .list-2009-item01{
                                                gap: 8px;
                                            }
                                            .list-2009-item02{
                                                padding-left: 0;
                                                padding-right: 140px;
                                                margin-top: 4px;
                                            }
                                        }
                                    }
                                    .swiper{
                                        padding-right: 16px;
                                    }
                                }
                                &.line-item{
                                    flex-direction: column;
                                    align-items: start;
                                    .box-img-lst{
                                        width: 100%;
                                    }
                                }
                            }

                            .dot {
                                margin: 0;
                                position: absolute;
                                left: -3px;
                                top: 20px;
                            }
                        }
                    }
                }
            }
        }
    }
`;
export default History;